import React, { useMemo, useState, useEffect } from 'react';
import { Segment, Header, Table, Icon, Input } from 'semantic-ui-react';
import { DriverRanking } from '../../models/client-ranking';
import {
  DriverSortColumn,
  DriverSortDirection,
  defaultDriverSort,
  sortDrivers,
  toggleSort,
} from '../../shared/utils/driver-ranking-sort';
import { filterDriversByQuery } from '../../shared/utils/driver-ranking-filter';
import {
  formatDriverPrizeCurrency,
  getDriverTotalPrize,
} from '../../shared/utils/driver-prize';
import './RankingTable.css';

interface DriversPerformanceTableProps {
  data: DriverRanking[];
  onDriverClick?: (driver: DriverRanking) => void;
}

const DriversPerformanceTable: React.FC<DriversPerformanceTableProps> = ({
  data,
  onDriverClick,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const initial = useMemo(() => defaultDriverSort(data), [data]);
  const [column, setColumn] = useState<DriverSortColumn>(initial.column);
  const [direction, setDirection] = useState<DriverSortDirection>(initial.direction);

  useEffect(() => {
    const next = defaultDriverSort(data);
    setColumn(next.column);
    setDirection(next.direction);
    setSearchQuery('');
  }, [data]);

  const filteredData = useMemo(
    () => filterDriversByQuery(data, searchQuery),
    [data, searchQuery]
  );

  const rows = useMemo(
    () => sortDrivers(filteredData, column, direction),
    [filteredData, column, direction]
  );

  const onHeaderClick = (clicked: DriverSortColumn) => {
    const next = toggleSort(column, direction, clicked);
    setColumn(next.column);
    setDirection(next.direction);
  };

  const sortIcon = (col: DriverSortColumn) => {
    if (col !== column) return <Icon name="sort" color="grey" />;
    return <Icon name={direction === 'asc' ? 'sort ascending' : 'sort descending'} />;
  };

  const formatCpf = (cpf: string) => {
    const cleaned = cpf.replace(/\D/g, '');
    if (cleaned.length !== 11) return cpf;
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  return (
    <Segment className="ranking-card drivers-performance-table">
      <div className="ranking-header">
        <div className="ranking-title">
          <Header as="h3">Ranking completo de motoristas</Header>
        </div>
        <span className="ranking-metric">
          Ordenação padrão: menor % carga motor = melhor. Clique nos headers para ordenar.
        </span>
      </div>

      <Input
        icon="search"
        iconPosition="left"
        placeholder="Buscar por motorista, CPF ou placa..."
        value={searchQuery}
        onChange={(_, { value }) => setSearchQuery(String(value))}
        className="ranking-search-input"
        style={{ marginBottom: '1rem', width: '100%', maxWidth: '420px' }}
      />

      {rows.length === 0 ? (
        <div className="ranking-empty">
          <p>
            {data.length > 0 && searchQuery.trim()
              ? 'Nenhum motorista encontrado para esta busca.'
              : 'Nenhum dado disponível para o período selecionado.'}
          </p>
        </div>
      ) : (
        <div style={{ maxHeight: '480px', overflowY: 'auto' }}>
          <Table sortable basic="very" className="ranking-table">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={1}>#</Table.HeaderCell>
                <Table.HeaderCell>Motorista</Table.HeaderCell>
                <Table.HeaderCell width={2}>Placa</Table.HeaderCell>
                <Table.HeaderCell
                  width={3}
                  sorted={column === 'motorLoadPercentage' ? (direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  onClick={() => onHeaderClick('motorLoadPercentage')}
                  style={{ cursor: 'pointer' }}
                >
                  Carga motor % {sortIcon('motorLoadPercentage')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  width={3}
                  sorted={column === 'inertiaPercentage' ? (direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  onClick={() => onHeaderClick('inertiaPercentage')}
                  style={{ cursor: 'pointer' }}
                >
                  Inércia % {sortIcon('inertiaPercentage')}
                </Table.HeaderCell>
                <Table.HeaderCell width={3}>Premiação</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((driver, index) => (
                <Table.Row
                  key={driver.driverId || `${driver.driverCpf}-${index}`}
                  onClick={() => onDriverClick && onDriverClick(driver)}
                  className={onDriverClick ? 'clickable-row' : ''}
                >
                  <Table.Cell>{driver.position}</Table.Cell>
                  <Table.Cell>
                    <div>{driver.driverName}</div>
                    <small style={{ color: '#888' }}>{formatCpf(driver.driverCpf)}</small>
                  </Table.Cell>
                  <Table.Cell>{driver.plate || '-'}</Table.Cell>
                  <Table.Cell>
                    <strong>{Number(driver.motorLoadPercentage).toFixed(1)}%</strong>
                  </Table.Cell>
                  <Table.Cell>{Number(driver.inertiaPercentage).toFixed(1)}%</Table.Cell>
                  <Table.Cell>
                    <strong>{formatDriverPrizeCurrency(getDriverTotalPrize(driver))}</strong>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </Segment>
  );
};

export default DriversPerformanceTable;
